import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useCart, getCartLineId } from '../context/CartContext';
import { useLocale } from '../context/LocaleContext';
import { useBestsellers } from '../hooks/useBestsellers';
import { calculateShipping, fetchPublicStoreSettings } from '../lib/storeSettings';
import { cssBackgroundImage } from '../utils/imageUrl';
import './Cart.css';

function Cart() {
  const { cartItems, removeFromCart, updateQuantity, totalPrice } = useCart();
  const { formatMoney } = useLocale();
  const { bestsellers, loading: recsLoading } = useBestsellers(6);
  const [shippingSettings, setShippingSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicStoreSettings()
      .then(settings => {
        if (!cancelled) setShippingSettings(settings);
      })
      .catch(() => {
        if (!cancelled) {
          setShippingSettings({ shipping_flat_rate: 0, shipping_free_threshold: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shippingCost = useMemo(
    () => calculateShipping(totalPrice, shippingSettings),
    [totalPrice, shippingSettings]
  );
  const orderTotal = totalPrice + shippingCost;

  const recommendations = useMemo(() => {
    const inBag = new Set(cartItems.map(item => item.id));
    return bestsellers.filter(product => !inBag.has(product.id)).slice(0, 3);
  }, [bestsellers, cartItems]);

  if (cartItems.length === 0) {
    return (
      <div className="cart-empty">
        <h1 className="store-title">Your bag is empty</h1>
        <p className="store-lede">
          When you find something for the house, it will live here.
        </p>
        <Link to="/shop" className="store-btn">
          Browse the shop
        </Link>
      </div>
    );
  }

  const freeThreshold = shippingSettings?.shipping_free_threshold;
  const shippingLabel =
    shippingCost === 0
      ? freeThreshold != null && totalPrice >= freeThreshold
        ? 'Free'
        : formatMoney(0)
      : formatMoney(shippingCost);

  return (
    <div className="cart-page">
      <header className="cart-header">
        <h1 className="store-title">Your bag</h1>
        <Link to="/shop" className="store-link">
          Continue shopping
        </Link>
      </header>

      <div className="cart-layout">
        <div className="cart-main">
          <div className="cart-list">
            {cartItems.map(item => {
              const cartLineId = item.cartLineId || getCartLineId(item.id, item.size);
              const lineTotal = Number(item.price) * item.quantity;
              const maxQty =
                item.availableStock != null && Number.isFinite(Number(item.availableStock))
                  ? Number(item.availableStock)
                  : null;
              const atMax = maxQty != null && item.quantity >= maxQty;

              return (
                <article key={cartLineId} className="cart-line">
                  <Link
                    to={`/product/${item.id}`}
                    className="cart-thumb"
                    style={{
                      background: cssBackgroundImage(item.image_url),
                    }}
                    aria-label={item.name}
                  >
                    {!item.image_url && <span>{item.category || 'Kit'}</span>}
                  </Link>

                  <div className="cart-line-body">
                    <div className="cart-line-info">
                      <h3>
                        <Link to={`/product/${item.id}`}>{item.name}</Link>
                      </h3>
                      {item.size && <p className="cart-line-meta">Size {item.size}</p>}
                      {item.variant && <p className="cart-line-meta">{item.variant}</p>}
                      <p className="cart-line-meta">{formatMoney(item.price)} each</p>
                      {maxQty != null && (
                        <p className="cart-line-meta">
                          {atMax ? `Max ${maxQty} in stock` : `${maxQty} in stock`}
                        </p>
                      )}
                    </div>

                    <div className="cart-line-controls">
                      <div className="cart-qty">
                        <button
                          type="button"
                          onClick={() => updateQuantity(cartLineId, item.quantity - 1)}
                          aria-label={`Decrease quantity of ${item.name}`}
                        >
                          −
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(cartLineId, item.quantity + 1)}
                          aria-label={`Increase quantity of ${item.name}`}
                          disabled={atMax}
                        >
                          +
                        </button>
                      </div>

                      <p className="cart-line-total">{formatMoney(lineTotal)}</p>
                    </div>

                    <button
                      type="button"
                      className="cart-remove"
                      onClick={() => removeFromCart(cartLineId)}
                    >
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <aside className="cart-summary" aria-label="Order summary">
          <h2 className="cart-summary-title">Summary</h2>

          <div className="cart-summary-rows">
            <div className="cart-summary-row">
              <span>Subtotal</span>
              <span>{formatMoney(totalPrice)}</span>
            </div>
            <div className="cart-summary-row">
              <span>Shipping</span>
              <span>{shippingSettings ? shippingLabel : '…'}</span>
            </div>
          </div>

          {freeThreshold != null && shippingCost > 0 && (
            <p className="cart-summary-note">
              Free shipping from {formatMoney(freeThreshold)}.
            </p>
          )}

          <p className="cart-summary-total">{formatMoney(orderTotal)}</p>

          <div className="cart-summary-actions">
            <Link to="/checkout" className="store-btn store-btn--block">
              Checkout
            </Link>
          </div>
        </aside>
      </div>

      {(recsLoading || recommendations.length > 0) && (
        <section className="cart-recs" aria-label="Recommended products">
          <p className="store-eyebrow">Complete the kit</p>
          {recsLoading && recommendations.length === 0 ? (
            <div className="cart-recs-grid" aria-hidden="true">
              {[0, 1, 2].map(key => (
                <div key={key} className="cart-recs-skeleton" />
              ))}
            </div>
          ) : (
            <div className="cart-recs-grid">
              {recommendations.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Cart;
